/**
 * Locale-aware weekday/month names come from Intl rather than a hand-written
 * table. The table only listed en/fr/de/sv, so the five other shipped locales
 * (es, it, nl, pl and even fr-fr) silently rendered English day names.
 * Formatters are cached because a month grid asks for seven names per render.
 */
const intlFormatterCache = new Map();

const dateFormatter = (locale, options) => {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = intlFormatterCache.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat(locale, options);
    } catch {
      formatter = new Intl.DateTimeFormat('en', options);
    }
    intlFormatterCache.set(key, formatter);
  }
  return formatter;
};

// Intl yields lowercase names, sometimes with an abbreviation dot ('lun.',
// 'janvier'); the UI has always shown 'Lun' / 'Janvier'.
const tidyDateName = (name) => {
  const trimmed = String(name ?? '').replace(/\.$/, '');
  return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : '';
};

export function useDateUtils() {
  /**
   * Parses a date string to a Date object using local time
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Date|null} Parsed date or null
   */
  const parseDate = (date) => {
    if (!date || !isValidDate(date)) return null;
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  /**
   * Validates a date string
   * @param {string} date - Date to validate
   * @returns {boolean} True if valid
   */
  const isValidDate = (date) => {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) return false;
    const [year, month, day] = date.split('-').map(Number);
    const parsedDate = new Date(year, month - 1, day);
    return (
      parsedDate.getFullYear() === year &&
      parsedDate.getMonth() === month - 1 &&
      parsedDate.getDate() === day
    );
  };

  /**
   * Adds days to a date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {number} days - Days to add
   * @returns {object} Object with short and full date
   */
  const addDaysToDate = (date, days) => {
    const dateObj = parseDate(date);
    if (!dateObj) return { short: '', full: null };

    dateObj.setDate(dateObj.getDate() + days);
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return {
      short: `${day}.${month}`,
      full: dateObj,
    };
  };

    const toMonthDay = (dateString, locale = 'en') => {
    if (!dateString) return '';
    try {
      // Parse date string and treat as local time
      const [year, month, day] = dateString.split(/[-T ]/)
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (isNaN(date.getTime())) return '';

      const monthName = date.toLocaleString(locale, { month: 'short' });
      const dayStr = String(date.getDate()).padStart(2, '0');
      
      return `${monthName}-${dayStr}`;
    } catch (e) {
      return ''; // Return empty string on error
    }
  };

  /**
   * Formats a date for display in result list
   * - Current year: "MMM-DD" (e.g., "Nov-15")
   * - Other years: "DD-MM-YY" (e.g., "15-11-24")
   * @param {string} dateString - Date string (ISO format)
   * @returns {string} Formatted date
   */
  const formatResultDate = (dateString, locale = 'en') => {
    if (!dateString) return '';
    try {
      // Extract date components manually to avoid timezone issues
      const dateStr = String(dateString);
      let year, month, day;
      
      // Handle various date formats
      if (dateStr.includes(' ')) {
        // Format: "YYYY-MM-DD HH:MM:SS" or "DD-MM-YYYY HH:MM:SS"
        const datePart = dateStr.split(' ')[0];
        
        if (datePart.includes('-')) {
          const parts = datePart.split('-');
          
          if (parts[0].length === 4) {
            // YYYY-MM-DD
            year = parseInt(parts[0]);
            month = parseInt(parts[1]);
            day = parseInt(parts[2]);
          } else {
            // DD-MM-YYYY
            day = parseInt(parts[0]);
            month = parseInt(parts[1]);
            year = parseInt(parts[2]);
          }
        }
      } else if (dateStr.includes('T')) {
        // ISO format: "YYYY-MM-DDTHH:MM:SS"
        const datePart = dateStr.split('T')[0];
        const parts = datePart.split('-');
        year = parseInt(parts[0]);
        month = parseInt(parts[1]);
        day = parseInt(parts[2]);
      } else if (dateStr.length >= 10 && dateStr.includes('-')) {
        // Just date: "YYYY-MM-DD"
        const parts = dateStr.split('-');
        if (parts[0].length === 4) {
          year = parseInt(parts[0]);
          month = parseInt(parts[1]);
          day = parseInt(parts[2]);
        }
      }
      
      if (!year || !month || !day) return '';

      const currentYear = new Date().getFullYear();

      if (year === currentYear) {
        // Current year: MMM-DD (localized month abbreviation)
        const monthName = new Date(year, month - 1, day)
          .toLocaleString(locale, { month: 'short' });
        const dayStr = String(day).padStart(2, '0');
        return `${monthName}-${dayStr}`;
      } else {
        // Other years: DD-MM-YY
        const dayStr = String(day).padStart(2, '0');
        const monthStr = String(month).padStart(2, '0');
        const yearStr = String(year).slice(-2);
        return `${dayStr}-${monthStr}-${yearStr}`;
      }
    } catch (e) {
      return '';
    }
  };

  /**
   * Calculates days to the specified weekday
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {number} targetWeekDay - Target weekday (1=Monday, 7=Sunday)
   * @param {boolean} excludeToday - Whether to exclude today
   * @returns {number} Days to add
   */
// In your useDateUtils.js composable
const getDaysToWeekDay = (startDate, targetWeekDay) => {
  const dateObj = parseDate(startDate);
  if (!dateObj) return 0;

  // Our system: 1=Mon, ..., 7=Sun
  const startDayOfWeek = dateObj.getDay() === 0 ? 7 : dateObj.getDay();

  // Calculate days to add (0-6)
  const daysToAdd = (targetWeekDay - startDayOfWeek + 7) % 7;
  
  return isNaN(daysToAdd) ? 0 : daysToAdd;
};

  const toYYYYMMDD = (date) => {
    /**
     * A MISSING date is not a date. `new Date(null)` is the epoch rather than an
     * Invalid Date, so without this guard the `isNaN` check below passes and an
     * absent value formats as 1969-12-31 (or 1970-01-01 east of UTC) — a real
     * date, rendered on a clinical timeline, where a blank belongs. An optional
     * field like a cycle's `endDate` is exactly where that happens. Every other
     * unusable input already returns null; this makes the missing case agree.
     */
    if (date === null || date === undefined || date === '') return null

    // If it's already a YYYY-MM-DD string, return it directly
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      return date.slice(0, 10); // Just extract the date part
    }
    
    // Check if the input is a valid Date object
    if (!(date instanceof Date) || isNaN(date)) {
      // If not, try to parse it as local date
      if (typeof date === 'string' && date.includes('-')) {
        // Parse as local date to avoid timezone issues
        const [year, month, day] = date.split(/[-T]/);
        if (year && month && day) {
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      const parsedDate = new Date(date);
      if (isNaN(parsedDate)) return null; // Still invalid, return null
      date = parsedDate;
    }
    
    // Use local methods to preserve the date as shown to the user
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /**
   * Converts a Date object to YYYY-MM-DD format using UTC time
   * Use this ONLY for server communication where UTC is required
   */
  const toYYYYMMDDUTC = (date) => {
    // Same guard as its local twin: a missing date must not become the epoch.
    if (date === null || date === undefined || date === '') return null

    // Check if the input is a valid Date object
    if (!(date instanceof Date) || isNaN(date)) {
      // If not, try to parse it
      const parsedDate = new Date(date);
      if (isNaN(parsedDate)) return null; // Still invalid, return null
      date = parsedDate;
    }
    
    // Use UTC methods for server communication
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format 'YYYY-MM' -> 'MonthName YYYY' in the caller's locale
  const formatMonthLabel = (yyyyMM, locale = 'en') => {
    if (!yyyyMM || !/^\d{4}-\d{2}$/.test(yyyyMM)) return ''
    const [y, m] = yyyyMM.split('-')
    const label = dateFormatter(locale, { year: 'numeric', month: 'long' })
      .format(new Date(Number(y), Number(m) - 1, 1))
    return tidyDateName(label)
  }

  // Accept 'DD MM YYYY' or 'DD-MM-YYYY' -> ISO 'YYYY-MM-DD'
  const displayToISO = (val) => {
    if (!val) return ''
    const cleaned = val.replace(/-/g, ' ').trim()
    const parts = cleaned.split(/\s+/)
    if (parts.length !== 3) return ''
    const [dd, mm, yyyy] = parts
    return `${yyyy}-${mm}-${dd}`
  }

  // ISO 'YYYY-MM-DD' -> 'DD-MM-YYYY'
  const isoToDisplay = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return ''
    const [y, m, d] = iso.split('-')
    return `${d}-${m}-${y}`
  }

  // Calculate age (years) from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return null
    const today = new Date()
    // Parse date-only strings (YYYY-MM-DD) as LOCAL, not UTC. `new Date('YYYY-MM-DD')`
    // is parsed as UTC midnight, which shifts the day back in UTC- zones and makes
    // the age wrong on the patient's birthday.
    let b
    if (typeof birthDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(birthDate)) {
      const [year, month, day] = birthDate.slice(0, 10).split('-').map(Number)
      b = new Date(year, month - 1, day)
    } else {
      b = new Date(birthDate)
    }
    if (isNaN(b)) return null
    let age = today.getFullYear() - b.getFullYear()
    const mdiff = today.getMonth() - b.getMonth()
    if (mdiff < 0 || (mdiff === 0 && today.getDate() < b.getDate())) age--
    return age
  }

    // ISO 'YYYY-MM-DD' -> 'DD MM YYYY' (spaces) for pickers that expect spaces
  const isoToDisplaySpaced = (iso) => isoToDisplay(iso).replace(/-/g, ' ')

    const getISOWeek = (d) => {
    if (!d) return null
    const src = d instanceof Date ? d : new Date(d)
    if (isNaN(src)) return null
    const date = new Date(src.getFullYear(), src.getMonth(), src.getDate())
    const dayNum = date.getDay() || 7
    date.setDate(date.getDate() + 4 - dayNum)
    const yearStart = new Date(date.getFullYear(), 0, 1)
    return Math.ceil((((date - yearStart) / 86400000) + 1) / 7)
  }

    // ISO week start (Monday) as a local Date object
  const getISOWeekStart = (d) => {
    if (!d) return null
    const src = d instanceof Date ? d : new Date(d)
    if (isNaN(src)) return null
    const date = new Date(src.getFullYear(), src.getMonth(), src.getDate())
    const day = date.getDay() || 7 // 1..7 (Mon..Sun)
    date.setDate(date.getDate() - (day - 1)) // back to Monday
    return date
  }

  // Return start/end Date objects (local time) for a given 'YYYY-MM'
  const getMonthBounds = (yyyyMM) => {
    if (!yyyyMM || !/^\d{4}-\d{2}$/.test(yyyyMM)) return { start: null, end: null }
    const [y, m] = yyyyMM.split('-').map(Number)
    const start = new Date(y, m - 1, 1, 0, 0, 0, 0)
    const end = new Date(y, m, 0, 23, 59, 59, 999)
    return { start, end }
  }

  // Get previous month string 'YYYY-MM'
  const prevMonthString = (yyyyMM) => {
    if (!/^\d{4}-\d{2}$/.test(yyyyMM)) return ''
    const [y, m] = yyyyMM.split('-').map(Number)
    const d = new Date(y, m - 2, 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  // Get next month string 'YYYY-MM'
  const nextMonthString = (yyyyMM) => {
    if (!/^\d{4}-\d{2}$/.test(yyyyMM)) return ''
    const [y, m] = yyyyMM.split('-').map(Number)
    const d = new Date(y, m, 1)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  const toDDMMYYYY = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return null;

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getWeekdayName = (jsDate, format = 'short', locale = 'en') => {
    const d = jsDate instanceof Date ? jsDate : new Date(jsDate)
    if (isNaN(d)) return ''
    const weekday = ['long', 'short', 'narrow'].includes(format) ? format : 'short'
    return tidyDateName(dateFormatter(locale, { weekday }).format(d))
  };
  
  return {
    parseDate,
    isValidDate,
    addDaysToDate,
    getDaysToWeekDay,
    toYYYYMMDD,
    toYYYYMMDDUTC,
    getWeekdayName,
    toDDMMYYYY,
    toMonthDay,
    formatResultDate,
    formatMonthLabel,
    displayToISO,
    isoToDisplay,
    calculateAge,
    getMonthBounds,
    prevMonthString,
    nextMonthString,
    isoToDisplaySpaced,
    getISOWeek,
    getISOWeekStart
  };
}